import { useEffect, useRef, useState } from "react";
import { getLogger } from "@hoteltradergit/ht-telemetry-ui-lib";
import { Container, Drawer } from "rsuite";
import { toast } from "react-toastify";
import { usePropertyStore } from "../../contexts/PropertyStoreContext";
import {
  searchUalEvents,
  getUalEventDetails,
  exportUalEvents,
} from "../../api";
import { saveFileResponse } from "../../utils/fileDownload";
import UserActivityForm from "./UserActivityForm";
import UserActivityResults from "./UserActivityResults";
import ActivityDetailsModal from "./ActivityDetailsModal";
import {
  INIT_FORM_VALUE,
  LOGS_AVAILABLE_NOTICE,
  DEFAULT_PAGE_SIZE,
  DETAILS_PAGE_SIZE,
  buildSearchSummary,
  buildSearchQuery,
  buildExportQuery,
} from "./utils";
import "./userActivity.scss";

const EMPTY_PAGE = {
  items: [],
  nextCursor: null,
  prevCursor: null,
  totalEstimate: 0,
  totalCapped: false,
};

// The search box re-queries the server; wait for a pause in typing first.
const SEARCH_DEBOUNCE_MS = 800;

const UserActivity = () => {
  const { properties } = usePropertyStore();

  const [isEdit, setIsEdit] = useState(true);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [formValue, setFormValue] = useState(INIT_FORM_VALUE);
  const [appliedFilters, setAppliedFilters] = useState(INIT_FORM_VALUE);
  const [page, setPage] = useState(EMPTY_PAGE);
  const [loading, setLoading] = useState(false);
  // Server-side column sort (null = backend default, date newest-first).
  const [sortColumn, setSortColumn] = useState(null);
  const [sortType, setSortType] = useState(null);
  // Server-side action-type filter (empty = every type).
  const [areaFilter, setAreaFilter] = useState([]);
  // Server-side free-text search. `appliedQ` is the term the current page was
  // fetched with; the debounce effect below re-queries when the box diverges.
  const [searchText, setSearchText] = useState("");
  const appliedQ = useRef("");
  // Monotonic request id — a slow response from an earlier query must not
  // overwrite the page a newer one already rendered.
  const requestSeq = useRef(0);

  const [selectedActivity, setSelectedActivity] = useState(null);
  const [details, setDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // react-toastify, like the other 40 files that surface errors — not rsuite's toaster.
  // rsuite 5.75's toaster lazily mounts its container through its own render helper, which sets
  // ReactDOM.__SECRET_INTERNALS_DO_NOT_USE_OR_YOU_WILL_BE_FIRED.usingClientEntryPoint. React 19
  // renamed that internal, so the object is undefined and every push throws — in the browser as
  // well as in tests, since the ESM build carries the same line.
  const notifyError = (text) => toast.error(text);

  const fetchPage = async (filters, pagination) => {
    const seq = ++requestSeq.current;
    const superseded = () => seq !== requestSeq.current;
    setLoading(true);
    try {
      const res = await searchUalEvents(
        buildSearchQuery(filters, { size: DEFAULT_PAGE_SIZE, ...pagination })
      );
      if (superseded()) return;
      // AppResponse envelope: { success, message, data: { items, ... } }
      const data = res?.data?.data || {};
      setPage({
        items: data.items || [],
        nextCursor: data.next_cursor || null,
        prevCursor: data.prev_cursor || null,
        totalEstimate: data.total_estimate || 0,
        totalCapped: Boolean(data.total_capped),
      });
    } catch (error) {
      if (superseded()) return;
      // The toast tells the user; this tells us. Swallowing the error entirely left a failed
      // search invisible in Rapid7, which is where UAL problems get diagnosed.
      getLogger().error("ual.search_failed", {
        type: "Error",
        page_url: window.location.href,
        error_desc: error?.message ?? String(error),
      });
      notifyError("Failed to load activity. Please try again.");
      setPage(EMPTY_PAGE);
    } finally {
      if (!superseded()) setLoading(false);
    }
  };

  const handleSearch = async (values) => {
    setAppliedFilters(values);
    setIsEdit(false);
    setIsDrawerOpen(false);
    setSortColumn(null); // new search resets to the backend default sort
    setSortType(null);
    setAreaFilter([]); // ...and to every action type
    setSearchText(""); // ...and clears the free-text box
    appliedQ.current = "";
    await fetchPage(values, {}); // first page, no cursor, default sort
  };

  const handleNext = () => {
    if (page.nextCursor) {
      fetchPage(appliedFilters, {
        cursor: page.nextCursor,
        direction: "next",
        sortBy: sortColumn,
        sortOrder: sortType,
        areas: areaFilter,
        q: appliedQ.current,
      });
    }
  };

  const handlePrev = () => {
    if (page.prevCursor) {
      fetchPage(appliedFilters, {
        cursor: page.prevCursor,
        direction: "prev",
        sortBy: sortColumn,
        sortOrder: sortType,
        areas: areaFilter,
        q: appliedQ.current,
      });
    }
  };

  // Column sort: re-query the whole result set from the first page in the new order.
  const handleSortColumn = (dataKey, type) => {
    setSortColumn(dataKey);
    setSortType(type);
    fetchPage(appliedFilters, {
      sortBy: dataKey,
      sortOrder: type,
      areas: areaFilter,
      q: appliedQ.current,
    });
  };

  // Action-type filter: server-side, so re-query from the first page (the existing
  // cursors belong to the previous filtered result set and can't be reused).
  const handleAreaFilterChange = (areas) => {
    setAreaFilter(areas);
    fetchPage(appliedFilters, {
      sortBy: sortColumn,
      sortOrder: sortType,
      areas,
      q: appliedQ.current,
    });
  };

  // Free-text search: debounced, server-side, and likewise back to the first page.
  useEffect(() => {
    if (isEdit) return undefined; // no search applied yet — nothing to refine
    const next = searchText.trim();
    if (next === appliedQ.current) return undefined; // already showing this term
    const timer = setTimeout(() => {
      appliedQ.current = next;
      fetchPage(appliedFilters, {
        sortBy: sortColumn,
        sortOrder: sortType,
        areas: areaFilter,
        q: next,
      });
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText, isEdit]);

  const handleRowClick = async (row) => {
    setSelectedActivity(row);
    setDetails(null);
    setDetailsLoading(true);
    try {
      const res = await getUalEventDetails(row.correlation_id, {
        summary_context: row,
        size: DETAILS_PAGE_SIZE,
      });
      // AppResponse envelope: payload (with items[]) lives in data.data
      setDetails(res?.data?.data || null);
    } catch (error) {
      getLogger().error("ual.details_failed", {
        type: "Error",
        page_url: window.location.href,
        error_desc: error?.message ?? String(error),
      });
      notifyError("Failed to load activity details.");
      setSelectedActivity(null);
    } finally {
      setDetailsLoading(false);
    }
  };

  const closeModal = () => {
    setSelectedActivity(null);
    setDetails(null);
  };

  // Excel export: the server re-runs the current search and streams back every matching
  // event, so the download reflects the filters on screen rather than the loaded page.
  const handleDownload = async () => {
    setDownloading(true);
    try {
      const res = await exportUalEvents(
        buildExportQuery(appliedFilters, {
          sortBy: sortColumn,
          sortOrder: sortType,
          areas: areaFilter,
          q: appliedQ.current,
        })
      );
      if (!saveFileResponse(res, "user_activity.xlsx")) {
        notifyError("The export came back empty. Please try again.");
      }
    } catch (error) {
      getLogger().error("ual.export_failed", {
        type: "Error",
        page_url: window.location.href,
        error_desc: error?.message ?? String(error),
      });
      notifyError("Failed to download the activity export.");
    } finally {
      setDownloading(false);
    }
  };

  const searchSummary = buildSearchSummary(appliedFilters, properties);

  return (
    <Container fluid className="user-activity-page">
      <div className="user-activity-page__heading">
        <span className="user-activity-page__beta">Beta</span>
        <h2 className="user-activity-page__title">User Activity</h2>
      </div>
      {isEdit && (
        <p className="user-activity-page__notice">{LOGS_AVAILABLE_NOTICE}</p>
      )}

      {isEdit ? (
        <UserActivityForm
          properties={properties}
          formValue={formValue}
          setFormValue={setFormValue}
          onSearch={handleSearch}
        />
      ) : (
        <>
          <UserActivityResults
            data={page.items}
            loading={loading}
            searchSummary={searchSummary}
            totalEstimate={page.totalEstimate}
            totalCapped={page.totalCapped}
            hasNext={Boolean(page.nextCursor)}
            hasPrev={Boolean(page.prevCursor)}
            onNext={handleNext}
            onPrev={handlePrev}
            onEditSearch={() => setIsDrawerOpen(true)}
            onRowClick={handleRowClick}
            sortColumn={sortColumn}
            sortType={sortType}
            onSortColumn={handleSortColumn}
            areaFilter={areaFilter}
            onAreaFilterChange={handleAreaFilterChange}
            searchText={searchText}
            onSearchTextChange={setSearchText}
            onDownload={handleDownload}
            downloading={downloading}
          />

          <Drawer
            placement="top"
            open={isDrawerOpen}
            closeButton={false}
            onClose={() => setIsDrawerOpen(false)}
          >
            <Drawer.Body className="user-activity-edit-drawer">
              <UserActivityForm
                properties={properties}
                formValue={formValue}
                setFormValue={setFormValue}
                onSearch={handleSearch}
                source="drawer"
                setIsDrawerOpen={setIsDrawerOpen}
              />
            </Drawer.Body>
          </Drawer>
        </>
      )}

      <ActivityDetailsModal
        open={Boolean(selectedActivity)}
        loading={detailsLoading}
        details={details}
        summary={selectedActivity}
        area={selectedActivity?.area}
        onClose={closeModal}
      />
    </Container>
  );
};

export default UserActivity;
